import { makeObservable, observable, computed, action, toJS } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import FilterState from '@features/categories/mobx/state/FilterState';
import { TFilterAbstract } from '@features/categories/typescript';
import { getCurrencyByRegion, getCurrencySymbolByCurrency, ICurrencyData } from '@utils/formatPrice';
import { getRegion } from '@utils/misc';
import { replaceBrowserSearchParams } from '@utils/replaceBrowserSearchParams';

export default class FilterRangeState extends FilterState {
  state: {
    min: number;
    max: number;
  } = { min: 1, max: 1000 };

  defaultValue: {
    min: number;
    max: number;
  } = { min: 1, max: 1000 };

  unit = '';

  currency: ICurrencyData = {} as ICurrencyData;

  defaultUnit = '';

  shouldSliderRefresh = false;

  constructor(filter: TFilterAbstract) {
    super(filter);
    makeObservable(this, {
      state: observable,
      queryURLString: computed,
      setValue: action,
      resetValue: action,
      toggleRefresh: action,
      value: computed,
      isSet: computed,
    });

    this.setInitialValue(filter);
    this.setInitialUnit();
  }

  setInitialUnit = () => {
    const region = getRegion();
    const currency = getCurrencyByRegion(region);
    this.defaultUnit = '';

    const ofUnitType = (unitType) => this.key.toLowerCase().includes(unitType);

    if (ofUnitType('price')) this.defaultUnit = currency || '';
    if (ofUnitType('width') || ofUnitType('height') || ofUnitType('depth')) this.defaultUnit = 'cm';

    this.unit = this.defaultUnit;
    this.currency = getCurrencySymbolByCurrency(this.defaultUnit);
  };

  setInitialValue = (filter) => {
    this.defaultValue = {
      min: Math.floor(filter.totalMin),
      max: Math.floor(filter.totalMax),
    };
    this.state = this.defaultValue;
  };

  get value() {
    return toJS(this.state);
  }

  get isSet() {
    return this.state.min !== this.defaultValue.min || this.state.max !== this.defaultValue.max;
  }

  setValue = (value: { min: number; max: number }, noURLUpdate = false) => {
    this.state = value;
    if (!noURLUpdate) {
      this.markUserActivity();
      this.setValueToURLString();
    }
  };

  resetValue = (noURLUpdate = false) => {
    this.setValue({ min: this.defaultValue.min, max: this.defaultValue.max }, noURLUpdate);
    this.toggleRefresh();
  };

  toggleRefresh = () => {
    this.shouldSliderRefresh = true;

    setTimeout(() => {
      this.shouldSliderRefresh = false;
    }, 1000);
  };

  setValueFromURLString = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paramValue = urlParams.get(this.queryName);
    if (paramValue) {
      const [paramValueMin, paramValueMax] = paramValue.split(',');

      if (paramValueMax && parseInt(paramValueMax, 10) > -1)
        this.state = { ...this.value, max: parseInt(paramValueMax, 10) };
      if (paramValueMin && parseInt(paramValueMin, 10) > -1)
        this.state = { ...this.value, min: parseInt(paramValueMin, 10) };
    }
  };

  removeValueFromURLString() {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(this.queryName);
    replaceBrowserSearchParams(urlParams);
  }

  setValueToURLString() {
    if (isEmpty(this.value)) {
      this.removeValueFromURLString();
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(this.queryName, [this.value.min, this.value.max].join(','));
    replaceBrowserSearchParams(urlParams);
  }

  get queryURLString(): string {
    const urlParams = new URLSearchParams();

    urlParams.append(this.queryName, [this.value.min, this.value.max].join(','));

    return decodeURI(urlParams.toString());
  }
}
